import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Grid } from '@mui/material'
import ModalComponent from '../../../components/modals'
import VerificationModal from '../../../components/modals/verificationModal'
import SimpleSelect from '../../../components/selects/simpleSelect'
import LaddaButton from '../../../components/buttons/laddaButton'
import CustomSnackbar from '../../../components/snackbar'
import { callApiAsync, getOrderNumbersForSurvey, checkSurveyExists, generateSurveyLink } from '../../../services/apiService'
import { icons } from '../../../constants'
import { getTodayDateTime } from '../../../utils/dateUtils'

const validationSchema = yup.object().shape({
    orderNumber: yup
        .mixed()
        .nullable()
        .required('El número de orden es requerido')
})

const GenerateSurveyLinkModal = ({ open, handleClose }) => {
    const [loading, setLoading] = useState(false)
    const [orderNumberOptions, setOrderNumberOptions] = useState([{ name: "Seleccionar", value: null }])
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState('success')
    const [openVerificationModal, setOpenVerificationModal] = useState(false)
    const [openSuccessModal, setOpenSuccessModal] = useState(false)
    const [generatedLink, setGeneratedLink] = useState('')
    const [selectedImportId, setSelectedImportId] = useState(null)

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            orderNumber: null
        }
    })

    const selectedOrderNumber = watch('orderNumber')

    useEffect(() => {
        if (open) {
            loadOrderNumbers()
            reset()
        }
    }, [open])

    useEffect(() => {
        if (selectedOrderNumber && selectedOrderNumber !== null) {
            const importId = typeof selectedOrderNumber === 'object' && selectedOrderNumber !== null
                ? selectedOrderNumber.value
                : selectedOrderNumber
            if (importId) {
                checkExistingSurvey(importId)
            }
        }
    }, [selectedOrderNumber])



    const loadOrderNumbers = async () => {
        await callApiAsync(
            () => getOrderNumbersForSurvey(),
            (resp) => {
                const data = resp?.data ?? resp
                const orders = Array.isArray(data) ? data : []
                setOrderNumberOptions([
                    { name: "Seleccionar", value: null },
                    ...orders
                ])
            },
            null
        )
    }

    const checkExistingSurvey = async (importId) => {
        if (!importId) return

        await callApiAsync(
            () => checkSurveyExists(importId),
            (resp) => {
                const data = resp?.data ?? resp
                if (data.exists && (data.surveyLinkToken || data.surveyOrderId)) {
                    // Si ya existe, mostrar el link existente en el modal de éxito
                    const token = data.surveyLinkToken || data.surveyOrderId
                    const existingUrl = `${window.location.origin}/foreign_trade_board/internal_client_satisfaction/national/answer/${token}`
                    setGeneratedLink(existingUrl)
                    // Cerrar el modal principal y mostrar el modal de éxito
                    handleClose()
                    setOpenSuccessModal(true)
                }
            },
            null
        )
    }

    const onSubmit = async (formData) => {
        // Extraer el ID del objeto select
        const importId = typeof formData.orderNumber === 'object' && formData.orderNumber !== null
            ? formData.orderNumber.value
            : formData.orderNumber

        if (!importId) {
            setSnackbarMessage('Por favor seleccione un número de orden')
            setSnackbarSeverity('error')
            setSnackbarOpen(true)
            return
        }

        // Verificar si ya existe una encuesta
        await callApiAsync(
            () => checkSurveyExists(importId),
            (resp) => {
                const data = resp?.data ?? resp
                if (data.exists && (data.surveyLinkToken || data.surveyOrderId)) {
                    // Si ya existe, mostrar el link existente en el modal de éxito
                    const token = data.surveyLinkToken || data.surveyOrderId
                    const existingUrl = `${window.location.origin}/foreign_trade_board/internal_client_satisfaction/national/answer/${token}`
                    setGeneratedLink(existingUrl)
                    // Cerrar el modal principal y mostrar el modal de éxito
                    handleClose()
                    setOpenSuccessModal(true)
                    return
                }

                // Si no existe, mostrar modal de verificación
                setSelectedImportId(importId)
                setOpenVerificationModal(true)
            },
            (error) => {

            }
        )
    }

    const handleConfirmGenerate = async () => {
        setOpenVerificationModal(false)
        setLoading(true)

        if (!selectedImportId) {
            setSnackbarMessage('Error: No se seleccionó un número de orden')
            setSnackbarSeverity('error')
            setSnackbarOpen(true)
            setLoading(false)
            return
        }

        // Generar el enlace
        await callApiAsync(
            () =>
                generateSurveyLink({
                    importId: selectedImportId,
                    creation_datetime: getTodayDateTime()
                }),
            (resp) => {
                const data = resp?.data ?? resp
                const fullUrl = `${window.location.origin}${data.surveyUrl}`
                setGeneratedLink(fullUrl)
                setOpenSuccessModal(true)
                setLoading(false)
            },
            (error) => {
 
            }
        )
    }

    const handleCopyLink = () => {
        if (generatedLink) {
            navigator.clipboard.writeText(generatedLink).then(() => {
                setSnackbarMessage('Link copiado al portapapeles')
                setSnackbarSeverity('success')
                setSnackbarOpen(true)
            }).catch(() => {
                setSnackbarMessage('Error al copiar el link')
                setSnackbarSeverity('error')
                setSnackbarOpen(true)
            })
        }
    }

    const handleCloseSuccessModal = () => {
        setOpenSuccessModal(false)
        setGeneratedLink('')
        handleClose()
        reset()
    }

    const handleCloseModal = () => {
        reset()
        handleClose()
    }

    return (
        <div>
            <ModalComponent
                open={open}
                handleClose={handleCloseModal}
                title="Generar Enlace para Orden de Importación"
                width="600px"
                actions={
                    <div className="w-complete mv-20">
                        <div className="j-center">
                            <LaddaButton
                                variant="contained"
                                onClick={handleSubmit(onSubmit)}
                                disabled={loading}
                            >
                                <div className="HContainer align-center">
                                    <p className="button_text">Generar Enlace</p>
                                </div>
                            </LaddaButton>
                        </div>
                    </div>
                }
            >
                <form>
                    <p className="select-form-label">Seleccionar Nro de Orden</p>
                    <SimpleSelect
                        name="orderNumber"
                        control={control}
                        label=""
                        options={orderNumberOptions}
                        error={errors.orderNumber}
                        margin="0"
                    />

                </form>
            </ModalComponent>

            <VerificationModal
                open={openVerificationModal}
                handleClose={() => setOpenVerificationModal(false)}
                alertText="¿Estás seguro de generar un enlace para esta orden de importación?"
                onConfirm={handleConfirmGenerate}
                onCancel={() => setOpenVerificationModal(false)}
                iconSeverity="warning"
                yesNo={true}
            />

            <ModalComponent
                open={openSuccessModal}
                handleClose={handleCloseSuccessModal}
                title=""
                width="600px"
                actions={
                    <div className="w-complete mv-20">
                        <div className="j-center">
                            <LaddaButton
                                variant="contained"
                                onClick={handleCloseSuccessModal}
                                backgroundColor="#6C5CE7"
                            >
                                <div className="HContainer align-center">
                                    <p className="button_text">Aceptar</p>
                                </div>
                            </LaddaButton>
                        </div>
                    </div>
                }
            >
                <div>
                    <h2 className="title-bio-details-chard" style={{
                        marginBottom: '20px',
                        textAlign: 'center',
                        color: '#6C5CE7',
                        fontSize: '20px'
                    }}>
                        Encuesta Satisfacción al Cliente Externo Creada con Exito
                    </h2>

                    <p className="detail-label" style={{
                        marginBottom: '15px',
                        fontSize: '14px',
                        color: '#666',
                        textAlign: 'center'
                    }}>
                        Link de acceso para los empleados encuestados
                    </p>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e0e0e0'
                    }}>
                        <img
                            src={icons.linkBlue}
                            alt="Link"
                            style={{ width: '20px', height: '20px' }}
                        />
                        <span style={{
                            fontSize: '14px',
                            color: '#333',
                            wordBreak: 'break-all',
                            flex: 1,
                            textAlign: 'center'
                        }}>
                            {generatedLink}
                        </span>
                        <span
                            onClick={handleCopyLink}
                            style={{
                                color: '#6C5CE7',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Copiar Link
                        </span>
                    </div>
                </div>
            </ModalComponent>

            <CustomSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />
        </div>
    )
}

export default GenerateSurveyLinkModal
